<template>
  <div class="rich-almanac-container">
    <el-card class="almanac-card">
      <!-- 头部信息 -->
      <div class="almanac-header">
        <h1>{{ almanac.gregorianDate }}</h1>
        <p class="lunar-date">{{ almanac.lunarDate }}</p>
        <div class="ganzhi-info">
          <el-tag type="info" size="medium">{{ almanac.ganzhiYear }}</el-tag>
          <el-tag type="info" size="medium">{{ almanac.ganzhiMonth }}</el-tag>
          <el-tag type="info" size="medium">{{ almanac.ganzhiDay }}</el-tag>
          <el-tag type="success" size="medium">{{ almanac.zodiac }}</el-tag>
          <el-tag type="warning" size="medium">{{
            almanac.constellation
          }}</el-tag>
        </div>
      </div>

      <!-- 运势概要 -->
      <div class="fortune-summary">
        <h3><i class="el-icon-star-on"></i> 今日运势</h3>
        <p>{{ almanac.fortuneSummary }}</p>
        <div class="fortune-tags">
          <el-tag type="primary" effect="dark">
            <i class="el-icon-sunny"></i> 幸运属相: {{ almanac.luckyAnimal }}
          </el-tag>
          <el-tag type="success" effect="dark">
            <i class="el-icon-brush"></i> 幸运色: {{ almanac.luckyColor }}
          </el-tag>
          <el-tag type="warning" effect="dark">
            <i class="el-icon-coin"></i> 幸运数字: {{ almanac.luckyNumber }}
          </el-tag>
          <el-tag type="danger" effect="dark">
            <i class="el-icon-warning-outline"></i> 特别提醒:
            {{ almanac.caution }}
          </el-tag>
        </div>
      </div>

      <!-- 传统信息 -->
      <el-row :gutter="20" class="traditional-info">
        <el-col :span="8">
          <div class="info-box">
            <h3><i class="el-icon-umbrella"></i> 日值星宿</h3>
            <p>{{ almanac.dayStar }}</p>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-box">
            <h3><i class="el-icon-magic-stick"></i> 胎神占方</h3>
            <p>{{ almanac.taiShen }}</p>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-box">
            <h3><i class="el-icon-moon"></i> 五行纳音</h3>
            <p>{{ almanac.fiveElements }}</p>
          </div>
        </el-col>
      </el-row>

      <!-- 五行纳音 -->
      <div class="five-elements">
        <h3><i class="el-icon-notebook-2"></i> 彭祖百忌</h3>
        <p>{{ almanac.pengZu }}</p>
      </div>

      <!-- 宜忌信息 -->
      <el-row :gutter="20" class="almanac-section">
        <el-col :span="12">
          <div class="suitable-box">
            <h3><i class="el-icon-circle-check"></i> 宜</h3>
            <div class="items">
              <el-tag
                v-for="(item, index) in almanac.suitable"
                :key="'suitable-' + index"
                type="success"
                effect="dark"
                size="medium"
              >
                {{ item }}
              </el-tag>
            </div>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="avoid-box">
            <h3><i class="el-icon-circle-close"></i> 忌</h3>
            <div class="items">
              <el-tag
                v-for="(item, index) in almanac.avoid"
                :key="'avoid-' + index"
                type="danger"
                effect="dark"
                size="medium"
              >
                {{ item }}
              </el-tag>
            </div>
          </div>
        </el-col>
      </el-row>

      <!-- 详细运势 -->
      <div class="detail-fortune">
        <h3><i class="el-icon-data-line"></i> 详细运势</h3>
        <el-row :gutter="20">
          <el-col :span="6">
            <div class="fortune-item love">
              <h4><i class="el-icon-user-solid"></i> 感情运势</h4>
              <p>{{ almanac.loveFortune }}</p>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="fortune-item career">
              <h4><i class="el-icon-office-building"></i> 事业运势</h4>
              <p>{{ almanac.careerFortune }}</p>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="fortune-item wealth">
              <h4><i class="el-icon-money"></i> 财富运势</h4>
              <p>{{ almanac.wealthFortune }}</p>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="fortune-item health">
              <h4><i class="el-icon-first-aid-kit"></i> 健康运势</h4>
              <p>{{ almanac.healthFortune }}</p>
            </div>
          </el-col>
        </el-row>
      </div>

      <!-- 神煞方位 -->
      <div class="gods-directions">
        <h3><i class="el-icon-place"></i> 神煞方位</h3>
        <el-row :gutter="10">
          <el-col
            v-for="(value, key) in almanac.godsDirections"
            :key="key"
            :span="4"
          >
            <div class="direction-item">
              <div class="direction-icon">
                <i :class="getDirectionIcon(key)"></i>
              </div>
              <div class="direction-name">{{ key }}</div>
              <div class="direction-value">{{ value }}</div>
            </div>
          </el-col>
        </el-row>
      </div>

      <!-- 时辰吉凶 -->
      <div class="hourly-luck">
        <h3><i class="el-icon-time"></i> 时辰吉凶</h3>
        <p class="best-time">
          <i class="el-icon-opportunity"></i> 今日吉时:
          {{ almanac.bestTime || "无" }}
        </p>
        <el-row :gutter="5">
          <el-col
            v-for="(value, key) in almanac.hourlyLuck"
            :key="key"
            :span="2"
          >
            <div
              class="hour-item"
              :class="{
                good: value === '吉',
                bad: value === '凶',
              }"
            >
              <div class="hour-name">{{ key }}</div>
              <div class="hour-value">{{ value }}</div>
            </div>
          </el-col>
        </el-row>
      </div>
    </el-card>
  </div>
</template>

<script>
import { getInfo } from "@/api/huangli";
export default {
  data() {
    return {
      almanac: {
        gregorianDate: "2025-11-11",
        lunarDate: "",
        ganzhiYear: "",
        ganzhiMonth: "",
        ganzhiDay: "",
        zodiac: "",
        constellation: "",
        suitable: [],
        avoid: [],
        godsDirections: {},
        hourlyLuck: {},
        dayStar: "",
        taiShen: "",
        pengZu: "",
        fiveElements: "",
        fortuneSummary: "",
        loveFortune: "",
        careerFortune: "",
        wealthFortune: "",
        healthFortune: "",
        luckyNumber: "",
        luckyColor: "",
        bestTime: "",
        caution: "",
      },
    };
  },
  created() {
    this.fetchAlmanacData();
  },
  methods: {
    fetchAlmanacData() {
      getInfo().then((res) => {
        this.almanac = res.data;
      });
    },
    getDirectionIcon(godName) {
      const icons = {
        喜神: "el-icon-star-on",
        财神: "el-icon-coin",
        福神: "el-icon-present",
        阳贵: "el-icon-sunny",
        阴贵: "el-icon-moon",
        煞神: "el-icon-warning",
      };
      return icons[godName] || "el-icon-help";
    },
  },
};
</script>

<style scoped>
.rich-almanac-container {
  font-family: "Microsoft YaHei", sans-serif;
  padding: 20px;
  background-color: #f5f5f5;
}

.almanac-card {
  max-width: 1000px;
  margin: 0 auto;
  background-color: #f9f5e9;
  border: 1px solid #d4b36a;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.almanac-header {
  text-align: center;
  padding: 20px 0;
  border-bottom: 1px dashed #d4b36a;
}

.date-selector {
  margin-bottom: 15px;
}

.almanac-header h1 {
  color: #8b0000;
  margin: 0;
  font-size: 28px;
  font-weight: bold;
}

.lunar-date {
  color: #8b0000;
  font-size: 18px;
  margin: 10px 0;
}

.ganzhi-info {
  margin-top: 15px;
}

.ganzhi-info .el-tag {
  margin: 0 5px;
  font-size: 16px;
  height: 32px;
  line-height: 32px;
}

.fortune-summary {
  padding: 20px;
  margin: 20px 0;
  background: linear-gradient(to right, #fdf6e3, #f9f5e9);
  border-radius: 4px;
}

.fortune-summary h3 {
  color: #8b0000;
  margin-bottom: 15px;
  font-size: 20px;
}

.fortune-summary p {
  font-size: 16px;
  line-height: 1.6;
  color: #333;
  margin-bottom: 15px;
}

.fortune-tags {
  margin-top: 10px;
}

.fortune-tags .el-tag {
  margin-right: 10px;
  font-size: 14px;
  height: 32px;
  line-height: 32px;
}

.traditional-info {
  margin: 25px 0;
}

.info-box {
  background: white;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: 100%;
  text-align: center;
}

.info-box h3 {
  color: #8b0000;
  margin-top: 0;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
  font-size: 18px;
}

.info-box p {
  font-size: 16px;
  margin-top: 15px;
  color: #333;
}

.five-elements {
  background: white;
  padding: 15px;
  margin: 20px 0;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.five-elements h3 {
  color: #8b0000;
  margin-top: 0;
  font-size: 18px;
}

.five-elements p {
  font-size: 16px;
  margin-top: 10px;
  color: #333;
}

.almanac-section {
  margin: 25px 0;
}

.suitable-box,
.avoid-box {
  background: white;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: 100%;
}

.suitable-box h3 {
  color: #67c23a;
  margin-top: 0;
  font-size: 18px;
}

.avoid-box h3 {
  color: #f56c6c;
  margin-top: 0;
  font-size: 18px;
}

.items {
  margin-top: 15px;
}

.items .el-tag {
  margin-right: 10px;
  margin-bottom: 10px;
  font-size: 16px;
  padding: 0 12px;
  height: 32px;
  line-height: 32px;
}

.detail-fortune {
  margin: 25px 0;
}

.detail-fortune h3 {
  color: #8b0000;
  margin-bottom: 20px;
  font-size: 20px;
}

.fortune-item {
  background: white;
  padding: 15px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: 100%;
}

.fortune-item h4 {
  margin-top: 0;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
  color: #333;
  font-size: 16px;
}

.fortune-item p {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
}

.fortune-item.love {
  border-left: 4px solid #f78989;
}

.fortune-item.career {
  border-left: 4px solid #789cf7;
}

.fortune-item.wealth {
  border-left: 4px solid #f7c578;
}

.fortune-item.health {
  border-left: 4px solid #78f7a8;
}

.gods-directions {
  margin: 25px 0;
}

.gods-directions h3 {
  color: #8b0000;
  margin-bottom: 15px;
  font-size: 20px;
}

.direction-item {
  background: white;
  padding: 15px 10px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  text-align: center;
  margin-bottom: 10px;
}

.direction-icon {
  font-size: 24px;
  color: #8b0000;
  margin-bottom: 5px;
}

.direction-name {
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
}

.direction-value {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.hourly-luck {
  margin: 25px 0;
}

.hourly-luck h3 {
  color: #8b0000;
  margin-bottom: 15px;
  font-size: 20px;
}

.best-time {
  font-size: 16px;
  color: #333;
  margin-bottom: 15px;
}

.hour-item {
  background: white;
  padding: 8px 5px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  text-align: center;
  margin-bottom: 10px;
}

.hour-item.good {
  background: #f0f9eb;
  border-left: 3px solid #67c23a;
}

.hour-item.bad {
  background: #fef0f0;
  border-left: 3px solid #f56c6c;
}

.hour-name {
  font-size: 12px;
  color: #666;
}

.hour-value {
  font-size: 16px;
  font-weight: bold;
}

.hour-item.good .hour-value {
  color: #67c23a;
}

.hour-item.bad .hour-value {
  color: #f56c6c;
}
</style>